import {
  Alert,
  Box,
  Button,
  Card,
  Group,
  Paper,
  Progress,
  ScrollArea,
  Table,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useRef, useState } from "react";
import { AlertCircle, Table as TableIcon } from "tabler-icons-react";
import * as XLSX from "xlsx";
import {
  useGetAllElevesQuery,
  useLazyGetStudentActivitiesQuery,
} from "../../redux/features/eleveApi";
import {
  SEGMENT_LABELS,
  SEGMENT_ORDER,
  computeCohortRow,
  detectSegment,
  mapWithConcurrency,
  toDakar,
  weekMondayOf,
} from "./utils/cohortCalculations";

// Colonnes affichées, groupées comme dans la spec (section 4), sans les
// colonnes de conversion (crédits) — donnée indisponible côté backend.
const COLUMN_GROUPS = [
  {
    label: "Identité",
    columns: [
      { key: "semaine_inscription", label: "Semaine", width: 100 },
      { key: "segment", label: "Segment", width: 90 },
      { key: "n_prospects", label: "N prospects", width: 90 },
    ],
  },
  {
    label: "Activation",
    columns: [
      { key: "n_j1", label: "J+1" },
      { key: "n_j3", label: "J+3" },
      { key: "n_j7", label: "J+7" },
      { key: "delai_median_1re_activite", label: "Délai médian (h)", width: 130 },
    ],
  },
  {
    label: "Profondeur",
    columns: [
      { key: "n_act2_j7", label: "≥2 act. J+7" },
      { key: "n_act2_j30", label: "≥2 act. J+30" },
    ],
  },
  {
    label: "Présence",
    columns: [
      { key: "n_sem1_act", label: "Sem. 1" },
      { key: "n_sem2_act", label: "Sem. 2" },
      { key: "n_sem3_act", label: "Sem. 3" },
      { key: "n_sem4_act", label: "Sem. 4" },
    ],
  },
  {
    label: "Régularité",
    columns: [
      { key: "n_engage_j30", label: "Engagé (≥8/30j)", width: 130 },
      { key: "n_habitude_j30", label: "Habitude (4 sem.)", width: 140 },
    ],
  },
];

const ALL_COLUMNS = COLUMN_GROUPS.flatMap((g) => g.columns);

const extractParentId = (student) =>
  student?.parent?.id ||
  student?.parentId ||
  student?.parrentId ||
  student?.parent_id ||
  student?.parentID ||
  student?.parent?.parentId ||
  student?.parent?.parent_id;

const extractCreatedAt = (student) =>
  student?.created_at || student?.createdAt || student?.date_inscription;

const extractClassName = (student) =>
  student?.classe?.name || student?.classe?.sub_title || null;

function CohortesPage() {
  const now = toDakar(new Date().toISOString());
  const defaultStart = now.subtract(12, "week").startOf("isoWeek");

  const [startDate, setStartDate] = useState(defaultStart.toDate());
  const [endDate, setEndDate] = useState(now.toDate());
  const [isComputing, setIsComputing] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [rows, setRows] = useState(null);
  const [computeError, setComputeError] = useState(null);
  const cancelledRef = useRef(false);

  const { data: elevesData, isLoading: isLoadingEleves } = useGetAllElevesQuery(
    { limit: 50000 },
  );

  const [triggerActivities] = useLazyGetStudentActivitiesQuery();

  const handleCancel = () => {
    cancelledRef.current = true;
    setIsComputing(false);
  };

  const handleCompute = async () => {
    if (!elevesData?.data?.length) return;

    cancelledRef.current = false;
    setIsComputing(true);
    setComputeError(null);
    setRows(null);
    setProgress({ done: 0, total: 0 });

    try {
      const rangeStart = toDakar(startDate.toISOString());
      const rangeEnd = toDakar(endDate.toISOString()).endOf("day");

      // 1) Regrouper les élèves par parent, déterminer T0 (premier enfant
      // ajouté) et le segment (niveau de ce premier enfant).
      const byParent = new Map();
      elevesData.data.forEach((student) => {
        const parentId = extractParentId(student);
        const createdAt = extractCreatedAt(student);
        if (!parentId || !createdAt) return;
        const at = toDakar(createdAt);
        if (!at || !at.isValid()) return;

        if (!byParent.has(parentId)) {
          byParent.set(parentId, { parentId, children: [] });
        }
        byParent.get(parentId).children.push({
          studentId: student?.id,
          createdAt: at,
          className: extractClassName(student),
        });
      });

      const parents = [];
      byParent.forEach(({ parentId, children }) => {
        const sorted = [...children].sort((a, b) =>
          a.createdAt.isBefore(b.createdAt) ? -1 : 1,
        );
        const first = sorted[0];
        const t0 = first.createdAt;
        if (t0.isBefore(rangeStart) || t0.isAfter(rangeEnd)) return;

        parents.push({
          parentId,
          t0,
          segment: detectSegment(first.className),
          weekMonday: weekMondayOf(t0),
          studentIds: sorted.map((c) => c.studentId).filter(Boolean),
          activities: [],
        });
      });

      if (parents.length === 0) {
        setRows([]);
        setIsComputing(false);
        return;
      }

      // 2) Récupérer les activités (4 types) de chaque enfant concerné,
      // avec un nombre limité de requêtes simultanées.
      const studentJobs = [];
      parents.forEach((p) => {
        p.studentIds.forEach((studentId) => {
          studentJobs.push({ studentId, parentId: p.parentId, t0: p.t0 });
        });
      });

      setProgress({ done: 0, total: studentJobs.length });

      const parentsById = new Map(parents.map((p) => [p.parentId, p]));

      await mapWithConcurrency(
        studentJobs,
        async (job) => {
          const res = await triggerActivities({
            studentId: job.studentId,
            startDate: job.t0.format("YYYY-MM-DD"),
            endDate: rangeEnd.format("YYYY-MM-DD"),
          }).unwrap();
          const items = Array.isArray(res?.data) ? res.data : [];
          const parent = parentsById.get(job.parentId);
          items.forEach((activity) => {
            const at = toDakar(activity?.date);
            if (at && at.isValid()) {
              parent.activities.push({ date: at });
            }
          });
        },
        6,
        (done, total) => setProgress({ done, total }),
        () => cancelledRef.current,
      );

      if (cancelledRef.current) {
        setIsComputing(false);
        return;
      }

      // 3) Grouper les parents par (semaine_inscription, segment) et
      // calculer chaque ligne.
      const buckets = new Map();
      parents.forEach((p) => {
        const key = `${p.weekMonday.format("YYYY-MM-DD")}__${p.segment}`;
        if (!buckets.has(key)) {
          buckets.set(key, { weekMonday: p.weekMonday, segment: p.segment, parents: [] });
        }
        buckets.get(key).parents.push(p);
      });

      const computedRows = Array.from(buckets.values())
        .map(({ weekMonday, segment, parents: bucketParents }) =>
          computeCohortRow({ weekMonday, segment, parents: bucketParents, now }),
        )
        .sort((a, b) => {
          if (a.semaine_inscription !== b.semaine_inscription) {
            return a.semaine_inscription < b.semaine_inscription ? -1 : 1;
          }
          return (
            SEGMENT_ORDER.indexOf(a.segment) - SEGMENT_ORDER.indexOf(b.segment)
          );
        });

      setRows(computedRows);
    } catch (err) {
      console.error("Erreur calcul cohortes:", err);
      setComputeError(
        "Une erreur est survenue pendant le calcul. Réessaie, ou réduis la plage de dates.",
      );
    } finally {
      setIsComputing(false);
    }
  };

  const handleExport = () => {
    if (!rows?.length) return;

    const headers = ["semaine_inscription", "segment", ...ALL_COLUMNS
      .filter((c) => !["semaine_inscription", "segment"].includes(c.key))
      .map((c) => c.key)];

    const exportRows = rows.map((row) =>
      headers.map((key) => {
        const value = row[key];
        // NULL (échéance pas atteinte) → cellule vide, jamais 0.
        return value === null || value === undefined ? "" : value;
      }),
    );

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...exportRows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Cohortes");
    const dateStr = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `cohortes_genimi_${dateStr}.xlsx`);
  };

  const renderCell = (row, key) => {
    const value = row[key];
    if (key === "segment") return SEGMENT_LABELS[value] || value;
    if (value === null || value === undefined) {
      return (
        <Text size="sm" color="dimmed">
          —
        </Text>
      );
    }
    if (key === "delai_median_1re_activite") {
      return <Text size="sm">{Math.round(value)}</Text>;
    }
    return <Text size="sm">{value}</Text>;
  };

  return (
    <Box>
      <Group spacing="sm" mb={4}>
        <ThemeIcon variant="light" color="blue" size={40} radius="md">
          <TableIcon size={22} />
        </ThemeIcon>
        <Title order={2}>Table de cohortes</Title>
      </Group>
      <Text size="sm" color="dimmed" mb="lg">
        Où décroche le tunnel GeniMi, semaine d'inscription par semaine
        d'inscription. Calculée à la demande, exportable en Excel.
      </Text>

      <Card withBorder radius="md" p="md" mb="lg">
        <Group align="flex-end" spacing="md">
          <DatePickerInput
            label="Semaine d'inscription — du"
            value={startDate}
            onChange={setStartDate}
            valueFormat="DD/MM/YYYY"
            maxDate={endDate}
            dropdownType="popover"
            popoverProps={{ withinPortal: true, zIndex: 2000, position: "bottom-start" }}
          />
          <DatePickerInput
            label="au"
            value={endDate}
            onChange={setEndDate}
            valueFormat="DD/MM/YYYY"
            minDate={startDate}
            maxDate={new Date()}
            dropdownType="popover"
            popoverProps={{ withinPortal: true, zIndex: 2000, position: "bottom-start" }}
          />
          {!isComputing ? (
            <Button onClick={handleCompute} loading={isLoadingEleves}>
              Calculer
            </Button>
          ) : (
            <Button color="red" variant="outline" onClick={handleCancel}>
              Annuler
            </Button>
          )}
          {rows?.length > 0 && (
            <Button variant="light" color="teal" onClick={handleExport}>
              Exporter en Excel
            </Button>
          )}
        </Group>

        {isComputing && (
          <Box mt="md">
            <Text size="xs" color="dimmed" mb={4}>
              {progress.total > 0
                ? `${progress.done} / ${progress.total} élèves traités`
                : "Préparation..."}
            </Text>
            <Progress
              value={progress.total > 0 ? (progress.done / progress.total) * 100 : 0}
              animate
            />
          </Box>
        )}
      </Card>

      {computeError && (
        <Alert icon={<AlertCircle size={16} />} color="red" mb="md">
          {computeError}
        </Alert>
      )}

      {rows === null ? (
        !isComputing && (
          <Text size="sm" color="dimmed" align="center" py={40}>
            Choisis une plage de dates puis clique sur "Calculer".
          </Text>
        )
      ) : rows.length === 0 ? (
        <Text size="sm" color="dimmed" align="center" py={40}>
          Aucun parent inscrit sur cette plage.
        </Text>
      ) : (
        <Paper withBorder radius="md" p={0} sx={{ overflow: "hidden" }}>
          <ScrollArea>
            <Table
              horizontalSpacing="sm"
              verticalSpacing="xs"
              sx={{ minWidth: 1200 }}
            >
              <thead>
                <tr>
                  {COLUMN_GROUPS.map((group) => (
                    <th
                      key={group.label}
                      colSpan={group.columns.length}
                      style={{
                        textAlign: "center",
                        fontSize: 11,
                        letterSpacing: 0.5,
                        textTransform: "uppercase",
                        color: "var(--mantine-color-dimmed)",
                        borderBottom: "1px solid #e9ecef",
                        background: "#f8f9fa",
                      }}
                    >
                      {group.label}
                    </th>
                  ))}
                </tr>
                <tr>
                  {ALL_COLUMNS.map((col) => (
                    <th key={col.key} style={{ minWidth: col.width || 70 }}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={`${row.semaine_inscription}__${row.segment}`}>
                    {ALL_COLUMNS.map((col) => (
                      <td key={col.key}>{renderCell(row, col.key)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </ScrollArea>
        </Paper>
      )}
    </Box>
  );
}

export default CohortesPage;
